import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, CalendarClock, CalendarDays, Users, FileText, Hammer, Tags, Clock, Settings, UserCog, Bell, Menu, LogOut, KeyRound } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { useFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Brand } from "@/layouts/CustomerLayout";

const NAV = [
  ["/admin", "Overview", LayoutDashboard, null],
  ["/admin/requests", "Job Requests", Inbox, "requests"],
  ["/admin/appointments", "Appointments", CalendarClock, "appointments"],
  ["/admin/calendar", "Calendar", CalendarDays, "appointments"],
  ["/admin/customers", "Customers", Users, "customers"],
  ["/admin/quotations", "Quotations", FileText, "quotations"],
  ["/admin/jobs", "Jobs", Hammer, "jobs"],
  ["/admin/services", "Services", Tags, "admin"],
  ["/admin/availability", "Availability", Clock, "admin"],
  ["/admin/notifications", "Notifications", Bell, null],
  ["/admin/users", "Users & Permissions", UserCog, "admin"],
  ["/admin/settings", "Settings", Settings, "admin"],
  ["/admin/account", "My Account", KeyRound, null],
];

function NavItems({ user, onNav }) {
  const items = NAV.filter(([, , , p]) => !p || (p === "admin" ? user?.role === "admin" : can(user, p)));
  return (
    <nav className="flex flex-col gap-0.5" aria-label="Administration">
      {items.map(([to, label, Icon]) => (
        <NavLink key={to} to={to} end={to === "/admin"} onClick={onNav} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}>
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: notif } = useFetch("/admin/notifications");
  const out = async () => { await logout(); nav("/login"); };
  return (
    <div className="min-h-screen bg-background">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-card focus:p-2">Skip to content</a>
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card md:flex">
        <div className="flex h-16 items-center border-b border-border px-5"><Brand sub="Administration" /></div>
        <div className="flex-1 overflow-y-auto p-3"><NavItems user={user} /></div>
        <div className="border-t border-border p-4 text-xs text-muted-foreground">
          <p className="font-medium text-foreground" data-testid="admin-user-name">{user?.name}</p>
          <p className="capitalize">{user?.role}</p>
        </div>
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 bg-card p-4">
          <SheetTitle className="mb-4"><Brand sub="Administration" /></SheetTitle>
          <NavItems user={user} onNav={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="md:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-card/95 px-4 backdrop-blur sm:px-6">
          <button className="rounded-md p-2 md:hidden" onClick={() => setOpen(true)} aria-label="Open navigation" data-testid="admin-mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <span className="hidden text-sm text-muted-foreground md:block">Europe/London · GBP</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/admin/notifications")} aria-label="Notifications" data-testid="header-notifications-btn">
              <Bell className="h-4 w-4" />
              {notif?.unread > 0 && <span className="ml-1 rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground" data-testid="unread-count">{notif.unread}</span>}
            </Button>
            <Button variant="outline" size="sm" onClick={out} data-testid="admin-logout-btn"><LogOut className="mr-1 h-4 w-4" />Sign out</Button>
          </div>
        </header>
        <main id="main" className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
